import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Chip,
  Avatar,
  ListItemText,
  ListItemAvatar
} from '@mui/material';
import {
  Save as SaveIcon,
  Public as CountryIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { getSystemSettings, saveSystemSettings } from '../../services/systemSettingsService';
import { SUPPORTED_COUNTRIES, Country, getCountryByCode } from '../../utils/countries';

const WhatsAppCountrySettings: React.FC = () => {
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadCurrentSettings();
  }, []);

  const loadCurrentSettings = async () => {
    try {
      setLoading(true);
      const settings = await getSystemSettings();
      const country = getCountryByCode(settings.whatsappCountryCode || '20');
      setSelectedCountry(country || SUPPORTED_COUNTRIES[0]);
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('فشل في تحميل الإعدادات');
      setSelectedCountry(SUPPORTED_COUNTRIES[0]); // مصر كافتراضي
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (countryCode: string) => {
    const country = getCountryByCode(countryCode);
    if (country) {
      setSelectedCountry(country);
    }
  };

  const handleSave = async () => {
    if (!selectedCountry) return;

    try {
      setSaving(true);
      setError(null);

      // الحصول على الإعدادات الحالية أولاً
      const currentSettings = await getSystemSettings();

      // تحديث الإعدادات مع القيم الجديدة
      const updatedSettings = {
        ...currentSettings,
        whatsappCountryCode: selectedCountry.code,
        whatsappCountryName: selectedCountry.name,
        phoneDigitsCount: selectedCountry.phoneDigits
      };

      await saveSystemSettings(updatedSettings);

      setSuccess('تم حفظ إعدادات الدولة بنجاح');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('فشل في حفظ الإعدادات');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>جاري تحميل إعدادات الدولة...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" mb={3}>
          <CountryIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            إعدادات الدولة لـ WhatsApp
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          اختر الدولة لتحديد رمز الهاتف الصحيح لرسائل WhatsApp
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Country Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>اختر الدولة</InputLabel>
          <Select
            value={selectedCountry?.code || ''}
            onChange={(e) => handleCountryChange(e.target.value)}
            label="اختر الدولة"
          >
            {SUPPORTED_COUNTRIES.map((country) => (
              <MenuItem key={country.code} value={country.code}>
                <Box display="flex" alignItems="center" width="100%">
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'transparent', fontSize: '1.5rem' }}>
                      {country.flag}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={country.name}
                    secondary={`+${country.code} - ${country.nameEn}`}
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Selected Country Info */}
        {selectedCountry && (
          <Card variant="outlined" sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                الدولة المختارة:
              </Typography>
              
              <Box display="flex" alignItems="center" mb={2}>
                <Typography variant="h4" sx={{ mr: 2 }}>
                  {selectedCountry.flag}
                </Typography>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedCountry.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCountry.nameEn}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  icon={<PhoneIcon />}
                  label={`رمز الدولة: +${selectedCountry.code}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  icon={<PhoneIcon />}
                  label={`عدد الأرقام: ${selectedCountry.phoneDigits}`}
                  color="secondary"
                  variant="outlined"
                />
              </Box>

              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>مثال على رقم الهاتف:</strong>
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                  +{selectedCountry.code}xxxxxxxxx
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || !selectedCountry}
          fullWidth
        >
          {saving ? 'جاري الحفظ...' : 'حفظ إعدادات الدولة'}
        </Button>

        {/* Info */}
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>ملاحظة:</strong> سيتم استخدام رمز الدولة المختار في جميع رسائل WhatsApp.
            تأكد من اختيار الدولة الصحيحة لضمان وصول الرسائل للعملاء.
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default WhatsAppCountrySettings;
